/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { MarkdownTransformer } from '@atlaskit/editor-markdown-transformer';
import { AutoDismissFlag, FlagGroup } from '@atlaskit/flag';
import Heading from '@atlaskit/heading';
import AiModelIcon from '@atlaskit/icon-lab/core/ai-model';
import RovoChatIcon from '@atlaskit/icon/core/rovo-chat';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import Image from '@atlaskit/image';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { ADFEncoder, ReactRenderer } from '@atlaskit/renderer';
import Spinner from '@atlaskit/spinner';
import Tabs, { Tab, TabList, useTabPanel } from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { FeedbackBanner } from '../../common/ui/feedback-banner/FeedbackBanner';
import MemoryList, { type MemoryItem } from '../../common/ui/memory-list';
import memoryListErrorIllustration from '../../common/ui/memory-list/assets/memory-list-error.svg';
import { MemorySubscreenFooterAddMemory } from '../memory-subscreen-footer-add-memory/MemorySubscreenFooterAddMemory';
import { MemorySubscreenFooter } from '../memory-subscreen-footer/MemorySubscreenFooter';

import profileMemoryEmptyIllustration from './assets/profile-memory-empty-illustration.svg';
import { messages } from './messages';

type FlagItem = {
	id: number;
	type: 'success' | 'error';
	title: string;
};

const styles = cssMap({
	root: {
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		height: '100%',
		minHeight: '0px',
		paddingBottom: token('space.200'),
		gap: token('space.200'),
	},
	headline: {
		flexShrink: 0,
		backgroundPosition: '0px 0px, 0px -5px, 0px -5px',
		backgroundImage: `linear-gradient(to right, ${token(
			'elevation.surface.sunken',
		)}, transparent 40%), linear-gradient(${token(
			'color.background.neutral',
		)} 1px, transparent 1px), linear-gradient(90deg, ${token(
			'color.background.neutral',
		)} 1px, transparent 1px)`,
		backgroundSize: '100% 100%, 20px 20px, 20px 20px',
		backgroundColor: token('elevation.surface.sunken'),
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		borderRadius: token('radius.large'),
	},
	iconContainer: {
		backgroundColor: token('color.background.neutral.bold'),
		borderRadius: token('radius.full'),
		color: token('color.text.inverse'),
		width: '32px',
		height: '32px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
	},
	tabPanel: {
		flex: '1 1 auto',
		minHeight: '0px',
		display: 'flex',
		flexDirection: 'column',
		paddingTop: token('space.200'),
	},
	profileMemoryContent: {
		flex: '1 1 auto',
		overflowY: 'auto',
		display: 'flex',
		flexDirection: 'column',
		paddingBottom: token('space.1000'),
	},
	profileMemorySpinner: {
		flex: '1',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
	profileMemoryCentered: {
		flex: '1',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		textAlign: 'center',
	},
	profileMemoryEmptyIllustrationWrapper: {
		display: 'flex',
		justifyContent: 'center',
	},
	profileMemoryEmptyHeading: {
		paddingBottom: token('space.100'),
	},
	profileMemoryEmptyMessage: {
		maxWidth: '440px',
	},
	profileMemoryEmptyButton: {
		paddingTop: token('space.200'),
		display: 'flex',
		justifyContent: 'center',
	},
	profileMemoryWrapper: {
		position: 'relative',
		flex: '1 1 auto',
		minHeight: '0px',
		display: 'flex',
		flexDirection: 'column',
	},
});

const CustomTabPanel = ({ children }: { children: ReactNode }) => {
	const tabPanelAttributes = useTabPanel();

	return (
		<div css={styles.tabPanel} {...tabPanelAttributes}>
			{children}
		</div>
	);
};

const adfEncoder = new ADFEncoder((schema) => new MarkdownTransformer(schema));

const TAB_ANALYTICS_IDS = ['profileMemory', 'explicitMemory'] as const;

interface MemoryModalSubscreenProps {
	explicitMemories?: MemoryItem[];
	onDeleteExplicitMemory: (memoryId: string) => Promise<void>;
	isExplicitMemoryLoading?: boolean;
	isExplicitMemoryError?: boolean;
	onExplicitMemoryRetry?: () => Promise<void>;
	profileMemory?: string | null;
	isProfileMemoryLoading?: boolean;
	isProfileMemoryError?: boolean;
	onProfileMemoryTabSelected?: () => void;
	onExplicitMemoryTabSelected?: () => void;
	feedbackUrl?: string;
	isInternalUser?: boolean;
	onShareFactWithRovo?: () => void;
	/**
	 * Count of pending memory edits to display in the footer badge.
	 * When undefined, the footer row is not shown.
	 */
	memoryEditsCount?: number | null;
	/**
	 * Callback invoked when the user clicks the "Manage memory edits" footer row.
	 */
	onManageMemoryEdits: () => void;
	/**
	 * Called when the user submits a memory edit. Receives the text the user typed.
	 * When provided, a floating edit button is shown on the profile memory tab.
	 */
	onCreateMemoryEdit: (text: string) => void;
	/**
	 * Whether a memory edit save is in progress. While true, the edit input shows a
	 * loading state and stays open; it closes once the save completes without an error.
	 */
	isSubmittingMemoryEdit?: boolean;
	/**
	 * Whether the last memory edit save failed. When true, the edit input shows an
	 * inline error and stays open so the user can retry.
	 */
	hasMemoryEditError?: boolean;
	/**
	 * Called when the user submits a new explicit memory. Receives the text typed and
	 * optional per-call success/error callbacks. Use callbacks instead of awaiting —
	 * useMutation never rejects, so a try/catch around onAddMemory would be dead code.
	 * When provided, an add button is shown on the explicit memory tab.
	 */
	onAddMemory?: (
		content: string,
		callbacks?: { onSuccess?: () => void; onError?: () => void },
	) => void;
	/**
	 * Whether an explicit memory creation is in progress.
	 */
	isCreatingMemory?: boolean;
}

export const MemoryModalSubscreen = ({
	explicitMemories = [],
	onDeleteExplicitMemory,
	isExplicitMemoryLoading: isExplicitLoading = false,
	isExplicitMemoryError: isExplicitError = false,
	onExplicitMemoryRetry: onExplicitRetry,
	profileMemory = null,
	isProfileMemoryLoading = false,
	isProfileMemoryError = false,
	onProfileMemoryTabSelected,
	onExplicitMemoryTabSelected,
	feedbackUrl,
	isInternalUser = false,
	onShareFactWithRovo,
	memoryEditsCount,
	onManageMemoryEdits,
	onCreateMemoryEdit,
	isSubmittingMemoryEdit = false,
	hasMemoryEditError = false,
	onAddMemory,
	isCreatingMemory = false,
}: MemoryModalSubscreenProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	useEffect(() => {
		sendAnalyticsEvent({
			eventType: 'screen',
			name: 'reviewMemorySubscreen',
		});
	}, [sendAnalyticsEvent]);

	const [flags, setFlags] = useState<FlagItem[]>([]);
	const flagIdCounter = useRef(0);
	const [selectedTabIndex, setSelectedTabIndex] = useState(0);

	const addFlag = useCallback((type: FlagItem['type'], title: string) => {
		const id = flagIdCounter.current++;
		setFlags((prev) => [{ id, type, title }, ...prev].slice(0, 5));
	}, []);

	const handleDismissFlag = useCallback((id: string | number) => {
		setFlags((prev) => prev.filter((f) => f.id !== Number(id)));
	}, []);

	const handleDeleteSuccess = useCallback(() => {
		addFlag('success', formatMessage(messages.deleteSuccessMessage));
	}, [addFlag, formatMessage]);

	const handleDeleteError = useCallback(() => {
		addFlag('error', formatMessage(messages.deleteErrorMessage));
	}, [addFlag, formatMessage]);

	const handleAddMemorySuccess = useCallback(() => {
		addFlag('success', formatMessage(messages.addSuccessMessage));
	}, [addFlag, formatMessage]);

	const handleAddMemoryError = useCallback(() => {
		addFlag('error', formatMessage(messages.addErrorMessage));
	}, [addFlag, formatMessage]);

	const shouldShowProfileMemoryFooter =
		!isProfileMemoryError && profileMemory !== null && fg('rovo_memories_enable_memory_edit');

	return (
		<Box xcss={styles.root} testId="memory-modal-subscreen">
			{feedbackUrl && isInternalUser && <FeedbackBanner feedbackUrl={feedbackUrl} />}
			<Inline xcss={styles.headline} space="space.150" alignBlock="center">
				<Box xcss={styles.iconContainer}>
					<AiModelIcon label="" size="medium" />
				</Box>
				<Stack space="space.025">
					<Heading size="medium">{formatMessage(messages.title)}</Heading>
					<Text as="p" color="color.text.subtle" size="medium">
						{formatMessage(
							selectedTabIndex === 0
								? messages.observationsSubtitle
								: messages.explicitMemorySubtitle,
						)}
					</Text>
				</Stack>
			</Inline>
			<Tabs
				id="memory-modal-subscreen-tabs"
				defaultSelected={0}
				onChange={(index) => {
					const tabName = TAB_ANALYTICS_IDS[index] ?? 'unknownTab';
					setSelectedTabIndex(index);

					sendAnalyticsEvent({
						action: 'clicked',
						actionSubject: 'tab',
						actionSubjectId: tabName,
						source: 'memoryModalSubscreen',
					});
					if (index === 0) {
						onProfileMemoryTabSelected?.();
					} else if (index === 1) {
						onExplicitMemoryTabSelected?.();
					}
				}}
			>
				<TabList>
					<Tab testId="memory-modal-subscreen-tab-observations">
						{formatMessage(messages.profileMemoryTab)}
					</Tab>
					<Tab testId="memory-modal-subscreen-tab-explicit">
						{formatMessage(messages.explicitFactsTab)}
					</Tab>
				</TabList>
				<CustomTabPanel>
					<div
						css={styles.profileMemoryWrapper}
						data-testid="memory-modal-subscreen-profile-memory-content"
					>
						<div css={styles.profileMemoryContent}>
							{isProfileMemoryLoading ? (
								<div css={styles.profileMemorySpinner}>
									<Spinner label={formatMessage(messages.loadingLabel)} size="medium" />
								</div>
							) : isProfileMemoryError ? (
								<div css={styles.profileMemoryCentered}>
									<Stack space="space.0">
										<Box xcss={styles.profileMemoryEmptyIllustrationWrapper}>
											<Image src={memoryListErrorIllustration} alt="" />
										</Box>
										<Text as="p" color="color.text.subtle" size="medium">
											{formatMessage(messages.profileMemoryErrorMessage)}
										</Text>
									</Stack>
								</div>
							) : profileMemory === null ? (
								<div css={styles.profileMemoryCentered}>
									<Stack space="space.0">
										<Box xcss={styles.profileMemoryEmptyIllustrationWrapper}>
											<Image src={profileMemoryEmptyIllustration} alt="" />
										</Box>
										<Box xcss={styles.profileMemoryEmptyHeading}>
											<Text size="large" weight="semibold" as="p">
												{formatMessage(messages.profileMemoryEmptyHeading)}
											</Text>
										</Box>
										<Box xcss={styles.profileMemoryEmptyMessage}>
											<Text size="medium" as="p">
												{formatMessage(messages.profileMemoryEmptyMessage)}
											</Text>
										</Box>
										{onShareFactWithRovo && (
											<Box xcss={styles.profileMemoryEmptyButton}>
												<Button onClick={onShareFactWithRovo} iconBefore={RovoChatIcon}>
													{formatMessage(messages.profileMemoryEmptyButton)}
												</Button>
											</Box>
										)}
									</Stack>
								</div>
							) : (
								<ReactRenderer document={adfEncoder.encode(profileMemory)} />
							)}
						</div>
					</div>
					{shouldShowProfileMemoryFooter && (
						<MemorySubscreenFooter
							pendingEditsCount={memoryEditsCount ?? undefined}
							onReviewPendingEdits={onManageMemoryEdits}
							onAddObservation={onCreateMemoryEdit}
							isSubmitting={isSubmittingMemoryEdit}
							hasError={hasMemoryEditError}
						/>
					)}
				</CustomTabPanel>
				<CustomTabPanel>
					<div
						css={styles.profileMemoryWrapper}
						data-testid="memory-modal-subscreen-explicit-memory-content"
					>
						<MemoryList
							memories={explicitMemories}
							isSearchable
							isLoading={isExplicitLoading}
							isError={isExplicitError}
							onDelete={onDeleteExplicitMemory}
							onRetry={onExplicitRetry}
							onDeleteSuccess={handleDeleteSuccess}
							onDeleteError={handleDeleteError}
						/>
					</div>
					{onAddMemory && fg('rovo_memories_explicit_memory_add_ui') && (
						<MemorySubscreenFooterAddMemory
							onAddMemory={onAddMemory}
							isCreatingMemory={isCreatingMemory}
							onAddSuccess={handleAddMemorySuccess}
							onAddError={handleAddMemoryError}
						/>
					)}
				</CustomTabPanel>
			</Tabs>
			<FlagGroup onDismissed={handleDismissFlag}>
				{flags.map((flag) => (
					<AutoDismissFlag
						key={flag.id}
						id={flag.id}
						icon={
							flag.type === 'success' ? (
								<StatusSuccessIcon label="success" color={token('color.icon.success')} />
							) : (
								<StatusErrorIcon label="error" color={token('color.icon.danger')} />
							)
						}
						title={flag.title}
					/>
				))}
			</FlagGroup>
		</Box>
	);
};
